import { DeliveryMode, Enrollment } from "api/resources/learningItems/types"
import FallbackProductImage from "assets/Placeholder/product-image-placeholder.svg"
import CpatLogo from "features/Cpat/assets/cpat-mini-logo.webp"
import {
  CPAT_PRODUCT_SLUG,
  learningItemSlugs,
  PgCTLProductSlugsEnum,
} from "utils/constants"
import { getRemoteMedia, RemoteMediaOptions } from "utils/getRemoteMedia"

import PgCTLExecutiveImage from "../../assets/pgctl/executive.webp"
import mEdImg from "../../assets/pgctl/itu-mini-logo-red.svg"
import PgCTLV1Image from "../../assets/pgctl/pgctl_1.0.webp"
import PgCTLProfessionalImage from "../../assets/pgctl/professional.webp"
import PgCTLStarterImage from "../../assets/pgctl/starter.webp"

export const isPgCTLProduct = (slug: string): slug is PgCTLProductSlugsEnum =>
  Object.values(PgCTLProductSlugsEnum).includes(slug as any)

export const getDeliveryMode = productSlug => {
  switch (productSlug) {
    case PgCTLProductSlugsEnum.starter:
      return DeliveryMode.STARTER
    case PgCTLProductSlugsEnum.executive:
      return DeliveryMode.EXECUTIVE
    case PgCTLProductSlugsEnum.professional:
      return DeliveryMode.PROFESSIONAL
  }
}

export const getPgCTLProductImage = (
  slug: string,
  options?: RemoteMediaOptions
) => {
  switch (slug) {
    case PgCTLProductSlugsEnum.starter:
      return getRemoteMedia(
        `product-thumbnails/professional-graduate-certificate-in-teaching-learning-starter`,
        options
      )
    case PgCTLProductSlugsEnum.professional:
      return getRemoteMedia(
        `product-thumbnails/professional-graduate-certificate-in-teaching-learning-professional`,
        options
      )
    case PgCTLProductSlugsEnum.executive:
      return getRemoteMedia(
        `product-thumbnails/professional-graduate-certificate-in-teaching-learning-executive`,
        options
      )
    default:
      return getRemoteMedia(
        `product-thumbnails/professional-graduate-certificate-in-teaching-learning`,
        options
      )
  }
}

export const getProductImage = (options: {
  defaultImage: string | undefined | null
  slug: string
  deliveryMode?: DeliveryMode | null
}) => {
  const { defaultImage, slug, deliveryMode } = options

  if (slug === CPAT_PRODUCT_SLUG) return CpatLogo

  if (slug === PgCTLProductSlugsEnum.starter) return PgCTLStarterImage
  if (slug === PgCTLProductSlugsEnum.professional) return PgCTLProfessionalImage
  if (slug === PgCTLProductSlugsEnum.executive) return PgCTLExecutiveImage

  if (slug === learningItemSlugs.pgctl) {
    if (deliveryMode === DeliveryMode.STARTER) return PgCTLStarterImage
    if (deliveryMode === DeliveryMode.PROFESSIONAL)
      return PgCTLProfessionalImage
    if (deliveryMode === DeliveryMode.EXECUTIVE) return PgCTLExecutiveImage
    if (deliveryMode === DeliveryMode.TDC) return PgCTLExecutiveImage

    return PgCTLV1Image
  }

  if (slug === learningItemSlugs.med) return mEdImg

  if (defaultImage) return defaultImage

  return FallbackProductImage
}

export const getDeliveryModeBadge = ({
  slug,
  deliveryMode,
}: {
  slug?: string
  deliveryMode?: DeliveryMode | null
}) => {
  if (
    slug === PgCTLProductSlugsEnum.starter ||
    deliveryMode === DeliveryMode.STARTER
  )
    return {
      text: "STARTER",
      bg: "bg-[linear-gradient(90deg,_#E0E7EC_43.5%,_#F3F6F9_66%,_#E0E7EC_89%)]",
      color: "text-[#334151]",
    }
  if (
    slug === PgCTLProductSlugsEnum.professional ||
    deliveryMode === DeliveryMode.PROFESSIONAL
  )
    return {
      text: "PROFESSIONAL",
      bg: "bg-[linear-gradient(90deg,_#C5DCFF_43.5%,_#DAF1FF_66%,_#C5DCFF_89%)]",
      color: "text-[#004696]",
    }
  if (
    slug === PgCTLProductSlugsEnum.executive ||
    deliveryMode === DeliveryMode.EXECUTIVE ||
    deliveryMode === DeliveryMode.TDC
  )
    return {
      text: "EXECUTIVE",
      bg: "bg-[linear-gradient(90deg,_#DDC5FF_43.5%,_#E9DAFF_66%,_#DDC5FF_89%)]",
      color: "text-[#3F0096]",
    }

  return null
}

export const getParentItem = (
  enrollments: Enrollment[] | undefined,
  identifier: string | number,
  key: "slug" | "id" = "slug"
) => {
  if (!enrollments) return null

  const currentItem = enrollments.find(x => x[key] === identifier)

  if (currentItem?.parents.length) {
    const parent = currentItem.parents[0]

    return {
      ...parent,
      isPgCTL: parent.slug === learningItemSlugs.pgctl,
      isMED: parent.slug === learningItemSlugs.med,
    }
  }

  return null
}
